import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Sliders, ShieldCheck, TrendingUp, Layers } from "lucide-react";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { ProductCard } from "@/components/ui/ProductCard";
import { vpsPlans } from "@/data/products";

const seo = pageSeo(
  "VPS Hosting — Configurable Virtual Servers",
  "Deploy flexible virtual private servers for websites, applications and development workloads, with a clear upgrade path as you grow."
);

const benefits = [
  { icon: Sliders, title: "Configurable Resources", body: "Choose the vCPU, RAM and storage that fit your workload." },
  { icon: ShieldCheck, title: "Administrative Control", body: "Full root or administrator access to manage your environment." },
  { icon: TrendingUp, title: "Upgrade Flexibility", body: "Move to more resources — or to dedicated infrastructure — without starting over." },
  { icon: Layers, title: "Multiple Workload Support", body: "Run websites, applications, databases or development environments." },
];

const workloads = [
  "Business websites",
  "Web applications",
  "Development environments",
  "Ecommerce platforms",
  "Databases",
  "Agency projects",
  "Internal systems",
];

export function VpsHostingPage() {
  useDocumentMeta(seo.title, seo.description);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="grid-backdrop absolute inset-0 opacity-30" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionEyebrow>Virtual Private Servers</SectionEyebrow>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-tight text-text-primary sm:text-5xl">
                Flexible Server Resources With Greater Control
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-text-secondary">
                Deploy virtual server environments for websites, applications, development
                projects and business workloads without moving directly to a dedicated server.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="#plans"
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
                >
                  View VPS Plans <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/contact"
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-medium text-text-primary transition hover:border-cyan/50"
                >
                  <MessageSquare className="h-4 w-4" /> Ask an Expert
                </Link>
              </div>
            </div>

            <div className="glass-panel rounded-card p-6">
              <div className="rounded-lg border border-dashed border-border p-4 text-center text-[11px] uppercase tracking-wide text-text-muted">
                Shared physical infrastructure layer
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {["VPS 01", "VPS 02", "VPS 03", "VPS 04", "VPS 05", "VPS 06"].map((v) => (
                  <div
                    key={v}
                    className="flex flex-col items-center gap-1.5 rounded-lg border border-border bg-navy-secondary py-4"
                  >
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-status-success" />
                    <span className="text-[10px] font-medium text-text-secondary">{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-center text-[11px] text-text-muted">
                Each environment is isolated, with its own allocated resources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {benefits.map((b) => (
            <div key={b.title} className="rounded-card border border-border bg-navy-card p-6">
              <b.icon className="h-5 w-5 text-cyan" strokeWidth={1.6} />
              <h3 className="mt-4 font-display text-base font-semibold text-text-primary">
                {b.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-text-secondary">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Suitable workloads */}
      <section className="border-y border-border bg-navy-secondary/40 py-16">
        <div className="mx-auto max-w-5xl px-5 text-center lg:px-8">
          <h2 className="font-display text-2xl font-semibold text-text-primary">
            Suitable Workloads
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {workloads.map((w) => (
              <span
                key={w}
                className="rounded-full border border-border bg-navy-card px-4 py-2 text-sm text-text-secondary"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <SectionEyebrow>Plans</SectionEyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
            Choose Your VPS Configuration
          </h2>
          <p className="mt-4 text-sm text-text-secondary">
            Specifications shown below are placeholders — full details and pricing are confirmed
            in the billing portal.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {vpsPlans.map((plan) => (
            <ProductCard key={plan.id} product={plan} />
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="border-t border-border bg-navy-secondary/40 py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-center font-display text-2xl font-semibold text-text-primary sm:text-3xl">
            Not Sure If VPS Is Right for You?
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="rounded-card border border-cyan/40 bg-navy-card p-6">
              <h3 className="font-display text-base font-semibold text-text-primary">VPS</h3>
              <p className="mt-2 text-sm text-text-secondary">
                Best for flexible and cost-conscious workloads requiring more control than
                standard hosting.
              </p>
            </div>
            <Link to="/dedicated-servers" className="focus-ring rounded-card border border-border bg-navy-card p-6 transition hover:border-cyan/40">
              <h3 className="font-display text-base font-semibold text-text-primary">
                Dedicated Server
              </h3>
              <p className="mt-2 text-sm text-text-secondary">
                Best for intensive workloads and organisations requiring dedicated resources.
              </p>
            </Link>
            <Link to="/cloud-solutions" className="focus-ring rounded-card border border-border bg-navy-card p-6 transition hover:border-cyan/40">
              <h3 className="font-display text-base font-semibold text-text-primary">Cloud</h3>
              <p className="mt-2 text-sm text-text-secondary">
                Best for flexible architectures, changing workloads and customised cloud
                deployments.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-5 py-20 text-center lg:px-8">
        <h2 className="font-display text-3xl font-semibold text-text-primary">
          Find the Right VPS Configuration
        </h2>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <a
            href="#plans"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-medium text-white transition hover:brightness-110"
          >
            Browse VPS Plans
          </a>
          <Link
            to="/contact"
            className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-medium text-text-primary transition hover:border-cyan/50"
          >
            Contact Infrastructure Team
          </Link>
        </div>
      </section>
    </>
  );
}
